import {
	Dependancy,
	TaskDefinition,
} from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';
import { formatYesNo } from 'src/utils/StringUtils';

export function makeTerminalRow(
	id: string,
	name: string,
	description: string
): TaskDefinition {
	return {
		taskId: id,
		taskName: name,
		taskType: name,
		taskDescription: description,
		owner: '-',
		batchExecutionRequired: false,
		regionalized: false,
		status: '-',
		asynchronous: false,
		averageExecutionTime: { days: 0, hours: 0, minutes: 0 },
		comment: '',
		createdBy: '',
		lastUpdateTime: 0,
		lastUpdatedBy: '',
		creationTime: 0,
		taskInputs: [],
		taskOutputs: [],
		latest: true,
		conditionalAttributes: [],
		taskDefinitionVersion: 0,
	};
}

export function getTaskDependencyNames(
	taskId: string,
	dependencies: Dependancy[],
	taskDefinitions: TaskDefinition[]
): string {
	return getTaskDependencyNamesList(taskId, dependencies, taskDefinitions).join(', ');
}

export function getTaskDependencyNamesList(
	taskId: string,
	dependencies: Dependancy[],
	taskDefinitions: TaskDefinition[]
): string[] {
	const deps = dependencies.filter((d) => d.end === taskId);

	return deps.map(
		(d) =>
			taskDefinitions.find((t) => t.taskId === d.start)?.taskName || d.start
	);
}

export const COLUMN_DEFINITIONS = [
	{
		id: 'taskName',
		header: 'Task Name',
		cell: (item: TaskDefinition) => item.taskName,
		sortingField: 'taskName',
		minWidth: '150px',
	},
	{
		id: 'taskType',
		header: 'Task Type',
		cell: (item: TaskDefinition) => item.taskType,
		sortingField: 'taskType',
		minWidth: '120px',
	},
	{
		id: 'taskDescription',
		header: 'Description',
		cell: (item: TaskDefinition) => item.taskDescription,
		sortingField: 'taskDescription',
		minWidth: '200px',
	},
	{
		id: 'dependencies',
		header: 'Dependencies',
		cell: () => '', // Rendered dynamically in the table component
		minWidth: '150px',
	},
	{
		id: 'batchExecutionRequired',
		header: 'Batch Execution',
		cell: (item: TaskDefinition) => formatYesNo(item.batchExecutionRequired),
		sortingField: 'batchExecutionRequired',
		minWidth: '100px',
	},
	{
		id: 'regionalized',
		header: 'Regionalized',
		cell: (item: TaskDefinition) => formatYesNo(item.regionalized ?? false),
		sortingField: 'regionalized',
		minWidth: '100px',
	},
	{
		id: 'status',
		header: 'Status',
		cell: (item: TaskDefinition) => item.status ?? '-',
		sortingField: 'status',
		minWidth: '100px',
	},
];

export const CONTENT_DISPLAY_OPTIONS = [
	{ id: 'taskName', label: 'Task Name', alwaysVisible: true },
	{ id: 'taskType', label: 'Task Type' },
	{ id: 'taskDescription', label: 'Description' },
	{ id: 'dependencies', label: 'Dependencies' },
	{ id: 'batchExecutionRequired', label: 'Batch Execution' },
	{ id: 'regionalized', label: 'Regionalized' },
	{ id: 'status', label: 'Status' },
];

export const PAGE_SELECTOR_OPTIONS = [
	{ value: 10, label: '10 Tasks' },
	{ value: 30, label: '30 Tasks' },
	{ value: 50, label: '50 Tasks' },
];

export const COLLECTION_PREFERENCES = {
	title: 'Preferences',
	confirmLabel: 'Confirm',
	cancelLabel: 'Cancel',
	pageSizePreference: {
		title: 'Select page size',
		options: PAGE_SELECTOR_OPTIONS,
	},
	contentDisplayPreference: {
		title: 'Column preferences',
		options: CONTENT_DISPLAY_OPTIONS,
	},
	stripedRowsPreference: {
		label: 'Striped rows',
		description: 'Select to add alternating shaded rows',
	},
};

const DEFAULT_VISIBLE = [
	'taskName',
	'taskType',
	'taskDescription',
	'dependencies',
	'batchExecutionRequired',
	'regionalized',
	'status',
];

export const DEFAULT_PREFERENCES = {
	pageSize: 30,
	contentDisplay: CONTENT_DISPLAY_OPTIONS.map(({ id }) => ({
		id,
		visible: DEFAULT_VISIBLE.includes(id),
	})),
};
